import React, { useEffect } from 'react';
import { Spin, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { TenantGlobalConfig } from '@/components/Intl/IntlContext/helper';
import IconFont from '@/components/IconFont';
import { BasicCardInfoItem, IguestInfo, MemberLevelItem } from '@/redux/reducers/guest';
import { DATE_TIME_FORMAT, DATE_FORMAT, MEMBER_CARD_STATUS_MAP, YEAR_FORMAT } from '@/config';
import { renderIntlLabel } from '@/utils';
import { isNil, parseFen2Currency } from '@/utils/number';
import { getTimeByUtcOffset } from '@/utils/intl';
import styles from './index.module.scss';

// 长期
const LONG_TIME_YEAR = '2099';

interface MemberInfoProps {
  guestInfo?: Partial<IguestInfo>;
  basicCardInfo?: BasicCardInfoItem;
  cardInfoLoading?: boolean;
}

const columns: ColumnsType<any> = [
  { title: renderIntlLabel('guest_card_label_cardName'), dataIndex: 'cardName' },
  {
    title: renderIntlLabel('guest_card_label_buy_card_time'),
    dataIndex: 'bindTime',
    render: (val) => (val ? getTimeByUtcOffset(val, DATE_TIME_FORMAT) : ''),
  },
  {
    title: renderIntlLabel('guest_card_label_store_of_buy_card'),
    dataIndex: 'commonCode',
    render: (val) => val || '—',
  },
  {
    title: renderIntlLabel('guest_card_label_amount_of_buy_card'),
    dataIndex: 'price',
    render: (val) => parseFen2Currency(val),
    align: 'right',
  },
  {
    title: renderIntlLabel('guest_card_label_indate_of_card'),
    dataIndex: 'startTime',
    render: (val, record) => {
      if (record?.endTime && getTimeByUtcOffset(record?.endTime, YEAR_FORMAT) === LONG_TIME_YEAR) {
        return `${val ? getTimeByUtcOffset(val, DATE_FORMAT) : ''} ${renderIntlLabel(
          'guest_card_label_qi',
        )}`;
      }
      if (val || record?.endTime) {
        return `${val ? getTimeByUtcOffset(val, DATE_FORMAT) : ''} ~ ${
          record?.endTime ? getTimeByUtcOffset(record?.endTime, DATE_FORMAT) : ''
        }`;
      }
      return '—';
    },
  },
  {
    title: renderIntlLabel('guest_card_label_status_of_member_card'),
    dataIndex: 'status',
    render: (val) => MEMBER_CARD_STATUS_MAP[val],
  },
];

const MemberInfo: React.FC<MemberInfoProps> = ({ basicCardInfo, cardInfoLoading }) => {
  return (
    <>
      <div className={styles.memberContainer}>
        <Spin spinning={cardInfoLoading}>
          <div className={styles.cardContainer}>
            <div className={styles.cardItem}>
              <div className={styles.icon}>
                <IconFont type="icon-chuzhiyue" />
              </div>
              <div>
                <div className={styles.label}>{renderIntlLabel('guest_card_label_balance')}</div>
                <div className={styles.value}>{parseFen2Currency(basicCardInfo.balance || 0)}</div>
              </div>
            </div>
            {!isNil(TenantGlobalConfig.getSystemConfig().xzBalance) && (
              <div className={styles.cardItem}>
                <div className={styles.icon}>
                  <IconFont type="icon-xuanzhuyue" />
                </div>
                <div>
                  <div className={styles.label}>
                    {renderIntlLabel('guest_card_label_xzBalance')}
                  </div>
                  <div className={styles.value}>
                    {parseFen2Currency(basicCardInfo.xzBalance || 0)}
                  </div>
                </div>
              </div>
            )}
            <div className={styles.cardItem}>
              <div className={styles.icon}>
                <IconFont type="icon-youhuiquan" />
              </div>
              <div>
                <div className={styles.label}>{renderIntlLabel('channel_crs_label_coupon')}</div>
                <div className={styles.value}>{basicCardInfo.totalCoupleCount}</div>
                <div>
                  {renderIntlLabel('guest_hotel_available_coupons')}:
                  <span>{basicCardInfo.availableCoupleCount}</span>
                </div>
              </div>
            </div>
            <div className={styles.cardItem}>
              <div className={styles.icon}>
                <IconFont type="icon-jiudianjifen" />
              </div>
              <div>
                <div className={styles.label}>{renderIntlLabel('guest_hotel_points')}</div>
                <div className={styles.value}>{basicCardInfo.points}</div>
                <div>
                  {renderIntlLabel('guest_hotel_available_points')}:
                  <span>{basicCardInfo.availablePoints}</span>
                </div>
                <div>
                  {renderIntlLabel('guest_hotel_points_due_this_month')}:
                  <span>{basicCardInfo.toExpirePoints}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-between" style={{ marginBottom: '24px', marginTop: '24px' }}>
            <div className="groupLabel">{renderIntlLabel('guest_title_list_of_member_card')}</div>
          </div>
          <Table
            rowKey="cardCode"
            columns={columns}
            dataSource={basicCardInfo?.memberCards}
            loading={false}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </Spin>
      </div>
    </>
  );
};

export default MemberInfo;
